/**
 * Shared victory-reward path for card combat (HEX-126).
 *
 * Extracted from `EncounterFlow` so the town-boss flow (`BossFlow`) grants the
 * same rewards as a wild encounter rather than gold alone: combat XP (+ full
 * heal on level-up), a tier-weighted card-drop prompt, and the HEX-117 level-up
 * 3-pick overlay. Both flows construct one `CombatRewards` and call
 * `grantVictoryRewards` on victory — no copy-pasted reward wiring.
 *
 * Owns the acquisition-drawer + `LevelUpOverlay` DOM lifecycle (HEX-149: every
 * gain is offered beside the loadout); the caller's teardown MUST call
 * `destroy()` so a still-mounted drawer is cleaned up.
 */

import type { Character } from '../core/character';
import type { Persistence } from '../persistence/Persistence';
import { XP_COMBAT_VICTORY, addXp, rollLevelUpOffers } from '../progression/xp';
import { rollCardDrop } from './cardDrops';
import { createRng } from './dice';
import { dieCard } from './diceRoster';
import { PLAYER_MAX_HP } from './player';
import { cardPrice } from './shopStock';
import type { Card, EnemyTier } from './types';
import { LevelUpOverlay } from './ui/LevelUpOverlay';
import { type AcquisitionSession, openAcquisition } from './ui/curationDrawer';

/**
 * Seed-derivation constants — kept stable so the drop and level-up rolls stay
 * deterministic + replayable, and distinct from the combat stream the engine
 * already consumed. Preserved from the original EncounterFlow values so existing
 * drop outcomes are unchanged.
 */
const DROP_SEED_XOR = 0x9e3b1f2d;
const LEVEL_UP_SEED_XOR = 0x13579bdf;
/** Per-level step mixed into the level-up seed on a multi-level jump (HEX-121),
 * so each level's offer set differs. Step 0 leaves the seed unchanged, keeping
 * the single-level outcome identical to before. */
const LEVEL_UP_STEP_XOR = 0x9e3779b1;

export interface CombatRewardDeps {
  readonly persistence: Pick<Persistence, 'saveCharacter'>;
}

export interface GrantVictoryRewardsArgs {
  readonly character: Character;
  /** Encounter/boss tier — weights the card-drop roll and combat XP award. */
  readonly tier: EnemyTier;
  /**
   * Base reward seed — pass `combatSeedFor(character, tile, worldSeed)`. The drop
   * and level-up seeds derive from it via stable xors.
   */
  readonly seed: number;
  readonly deps: CombatRewardDeps;
}

export class CombatRewards {
  private acquiring: AcquisitionSession | null = null;
  private levelUpOverlay: LevelUpOverlay | null = null;

  constructor(private readonly container: HTMLElement) {}

  /**
   * Award combat XP, then offer the tier-weighted card drop and (on a level
   * crossing) the HEX-117 reward overlay. XP is awarded + persisted FIRST — the
   * full heal lands and `addXp`'s `levelsGained` decides how many overlays show
   * — then the drop prompt, then ONE level-up overlay per level gained, in
   * sequence, each with a fresh offer set (HEX-121: a big award, e.g. a boss
   * kill spanning two levels, must grant a pick for every level). Matches the
   * original EncounterFlow ordering.
   */
  async grantVictoryRewards(args: GrantVictoryRewardsArgs): Promise<void> {
    const { character, tier, seed, deps } = args;
    const prevLevel = character.level ?? 1;
    const { levelsGained } = addXp(character, XP_COMBAT_VICTORY[tier]);
    // Full heal lands once on any level gain (PLAYER_MAX_HP is the cap, so this
    // is inherently clamped) regardless of how many levels were crossed.
    if (levelsGained > 0) character.hp = PLAYER_MAX_HP;
    await deps.persistence.saveCharacter(character);

    const dropSeed = seed ^ DROP_SEED_XOR;
    const card = rollCardDrop(tier, createRng(dropSeed));
    if (card !== null) await this.runDropAcquisition(character, card, deps);

    // One overlay per level gained. The i=0 seed equals the historical
    // single-level seed so existing outcomes are unchanged; later levels mix in
    // the step index for distinct offers. Each overlay shows the intermediate
    // level reached (prevLevel + 1 + i), not just the final level.
    const baseLevelSeed = dropSeed ^ LEVEL_UP_SEED_XOR;
    for (let i = 0; i < levelsGained; i++) {
      const levelReached = prevLevel + 1 + i;
      await this.runLevelUpOverlay(
        character,
        levelReached,
        (baseLevelSeed ^ (i * LEVEL_UP_STEP_XOR)) | 0,
        deps,
      );
    }
  }

  destroy(): void {
    this.acquiring?.destroy();
    this.acquiring = null;
    this.levelUpOverlay?.destroy();
    this.levelUpOverlay = null;
  }

  /** Level-up reward overlay: roll 3 offers, wait for the player to pick one,
   * then route the chosen card/die through the shared acquisition drawer (offer
   * beside the loadout, curate-and-place — HEX-149) so a full grid no longer
   * silently declines the reward. */
  private runLevelUpOverlay(
    character: Character,
    level: number,
    seed: number,
    deps: CombatRewardDeps,
  ): Promise<void> {
    return new Promise((resolve) => {
      const offers = rollLevelUpOffers(createRng(seed));
      const overlay = new LevelUpOverlay(this.container);
      this.levelUpOverlay = overlay;
      const finish = () => {
        overlay.destroy();
        this.levelUpOverlay = null;
        resolve();
      };
      overlay.start({
        level,
        offers,
        onPick: async (offer) => {
          const item = offer.kind === 'card' ? offer.card : dieCard(offer.die);
          const session = openAcquisition(this.container, character, {
            item,
            declineLabel: 'Forgo',
          });
          this.acquiring = session;
          await session.done;
          this.acquiring = null;
          await deps.persistence.saveCharacter(character);
        },
        onDone: finish,
      });
    });
  }

  /** Present the card drop beside the loadout (HEX-149): place it into a chosen
   * cell, curate to make room, or take its gold value (card-or-gold, HEX-72). */
  private runDropAcquisition(
    character: Character,
    card: Card,
    deps: CombatRewardDeps,
  ): Promise<void> {
    return (async () => {
      const goldValue = cardPrice(card);
      const session = openAcquisition(this.container, character, {
        item: card,
        declineLabel: `Take ${goldValue} gold`,
      });
      this.acquiring = session;
      const outcome = await session.done;
      this.acquiring = null;
      if (outcome === 'declined') {
        // Card-or-gold (HEX-72): forgo the card for its market value in coin.
        character.gold += goldValue;
      }
      await deps.persistence.saveCharacter(character);
    })();
  }
}
